/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemKind } from "../../shared/contracts";
import type { Ball } from "../items/ball/ball";
import type { HitObject } from "../physics/hit-object";
import type { Event } from "./event";
import type { IItem } from "./iitem";

/**
 * Receives every event that an item fires.
 *
 * Upstream the events go to the table script. Here the player implements
 * this interface and turns the events into `PhysicsEvent` values for the rules.
 */
export interface EventSink {
  onItemEvent(proxy: EventProxy, event: Event, params: readonly unknown[]): void;
}

/**
 * The event port of one item.
 *
 * A hit object holds a proxy and calls `fireGroupEvent` when a ball hits it.
 * The proxy passes the event on to the sink.
 */
export class EventProxy {
  /**
   * while playing and the ball hits the mesh the hit threshold is updated here
   */
  public currentHitThreshold: number = 0;

  /** The item that owns this proxy. */
  public readonly item: IItem;
  public readonly kind: ItemKind;
  private readonly sink: EventSink | undefined;

  /**
   * Logic executed on collision.
   *
   * This replaces the dreaded object casts in the original code where the hit
   * logic must be aware of the underlying object.
   */
  public onCollision?: (obj: HitObject, ball: Ball, dot: number) => void;

  /**
   * If implemented and false is returned, the hit test is skipped.
   */
  public abortHitTest?: () => boolean;

  constructor(item: IItem, kind: ItemKind, sink?: EventSink) {
    this.item = item;
    this.kind = kind;
    this.sink = sink;
  }

  public getName(): string {
    return this.item.getName();
  }

  public fireVoidEvent(e: Event): void {
    this.sink?.onItemEvent(this, e, []);
  }

  public fireVoidEventParm(e: Event, ...params: unknown[]): void {
    this.sink?.onItemEvent(this, e, params);
  }

  public fireGroupEvent(e: Event): void {
    this.sink?.onItemEvent(this, e, []);
  }
}
